/**
 * LongerRoad.js — "The Longer Road · Tirthan Valley '26"
 * Full-bleed cinematic hero + hand-drawn SVG (SUV silhouette, bonfire, layered peaks)
 * layered over verified Unsplash imagery. Zero brand marks.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mountain, MessageCircle, Check, X, Image as ImageIcon, Send } from 'lucide-react';
import LongerRoadRequestModal from '../components/LongerRoadRequestModal';

const pf = { fontFamily: "'Playfair Display', Georgia, serif" };
const GH_BLUE = '#2C5F8D';
const WA_MSG = encodeURIComponent("Hi — I'd like to know more about The Longer Road · Tirthan Valley '26. My name is _____ , driving from _____ .");
const WA_1 = `https://wa.me/919871155963?text=${WA_MSG}`;
const WA_2 = `https://wa.me/919811763700?text=${WA_MSG}`;

/* Verified imagery. Only IDs I've confirmed resolve. */
const IMG_HERO = 'https://gohimalayan.com/tirthan_hero.jpg';   // Actual Tirthan Valley hero (production)
const IMG_TIRTHAN = 'https://customer-assets.emergentagent.com/job_3135eaa2-4743-4a37-8f30-3aa61a9c991c/artifacts/ub9tfiva_Riverside%20Cottage%20%40%20Tirthan%20Valley.jpg';
const IMG_RIDGE = 'https://images.unsplash.com/photo-1465188162913-8fb5709d6d57?auto=format&fit=crop&w=1600&q=80';

/* --- SVG assets ------------------------------------------------------ */

/** SUV silhouette · removed on request — the illustration didn't read as an SUV. */

/** Layered valley silhouettes + winding road + tiny SUV climbing it. */
/* Removed on request — the illustrated valley band was replaced by a real Tirthan photo band. */

const INCLUDED = [
  '3 nights, double-occupancy · Bluehouse by the River + one boutique sister property (both riverside)',
  'Unhurried breakfasts at the property, every morning',
  'One planned lunch outside the property (Friday · a mountain village halt)',
  'One planned lunch outside the property (Saturday · at Jalori Pass)',
  'A bonfire supper at the property on Saturday evening',
  'GST & service',
];
const EXCLUDED = [
  'Fuel, tolls & road snacks',
  'Alcohol — BYOB · we encourage a bottle to share around the fire',
  'Anything you do outside these four days',
];
const SCHEDULE = [
  {
    day: 'Thursday',
    title: 'Delhi at first light · into the valley by evening',
    body: 'One flag-off from Delhi-NCR at 4:30 am, just before the city wakes. Two short stops on the plains \u2014 chai near Karnal, a proper breakfast near Chandigarh. By early afternoon we\u2019re on the mountain leg; by 5 pm the SUVs are parked on gravel and the river is the only thing you can hear.',
    highlight: 'The main thing \u2014 a quiet sundown welcome on the riverbank, and dinner on the terrace by 8. Nothing else, on purpose.'
  },
  {
    day: 'Friday',
    title: 'Slow morning · long lunch up the valley',
    body: 'An unhurried breakfast at the property. Sit with your coffee for as long as you want. Around 11 we roll up the valley in convoy, no rush. Back at the property by golden hour \u2014 open evening, the deck, the fire pit.',
    highlight: 'The main thing \u2014 a long, slow mountain lunch either at Barthad or Sarchi (we pick the one that\u2019s weather-happy that day). Local menu, our people know theirs.'
  },
  {
    day: 'Saturday',
    title: 'Up to Jalori Pass · fire back at the property',
    body: 'Another easy breakfast on the deck. Late morning we drive up to Jalori Pass \u2014 the road climbs, the deodars thin, the light gets cleaner. Back down through the afternoon. The evening is ours.',
    highlight: 'The main thing \u2014 lunch at Jalori Pass (10,800 ft), sorted. And later, the bonfire supper back at the property. Mountain trout, woodsmoke, a bottle passed around.'
  },
  {
    day: 'Sunday',
    title: 'Home, slowly',
    body: 'One last unhurried breakfast at the property. Bills settled by 10. Roll out when you\u2019re ready \u2014 the road down is easier than the road up.',
    highlight: 'The main thing \u2014 a short farewell walk along the water before you drive out. Nothing planned. Nobody rushing.'
  },
];

/* An open menu — mention-only, not itinerary-fixed */
const OPTIONAL_EXPERIENCES = [
  { name: 'Chehni Kothi', note: 'The 1,500-year-old free-standing tower, still taller than the deodars around it. Short drive, then a walk.' },
  { name: 'Choi Waterfall', note: 'A quiet, sacred waterfall with a natural pool. An easy two-kilometre walk in.' },
  { name: 'Great Himalayan National Park', note: 'UNESCO site on the far side of the valley. Guided day-hikes for those who want depth.' },
  { name: 'The Serolsar Lake trail', note: 'A five-kilometre forest walk beyond Jalori Pass. Best done in the morning light.' },
  { name: 'Village walks · Sarchi & Gushaini', note: 'Wooden Himachali homes, slate roofs, apple orchards. Best in the golden hour.' },
  { name: 'Trout fishing on the Tirthan', note: 'Catch-and-release, with a local guide. Brown and rainbow trout in the deep pools.' },
];

export default function LongerRoad() {
  const [requestOpen, setRequestOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#0b1220]" data-testid="longer-road-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-stone-950/70 backdrop-blur-md border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Mountain className="w-5 h-5 text-amber-200" />
            <span className="text-sm font-bold tracking-tight text-white" style={pf}>GoHimalayan</span>
          </Link>
          <Link to="/itineraries" className="flex items-center gap-1.5 text-white/50 hover:text-white text-xs">
            <ArrowLeft className="w-3.5 h-3.5" /> All itineraries
          </Link>
        </div>
      </nav>

      {/* Hero · dusk peaks image + SVG SUV on the road */}
      <section className="relative min-h-[96vh] flex items-end overflow-hidden bg-[#0b1220]">
        <img src={IMG_HERO} alt="Mountains at golden hour"
             className="absolute inset-0 w-full h-full object-cover"
             style={{ filter: 'brightness(0.55) saturate(1.15) contrast(1.05)' }} />
        {/* Colour wash — valley dusk */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(11,26,44,0.65) 0%, rgba(11,26,44,0.15) 40%, rgba(180,100,50,0.20) 65%, rgba(11,18,32,0.95) 100%)'
        }} />
        {/* Moon */}
        <div className="absolute top-16 right-20 w-24 h-24 rounded-full opacity-90"
             style={{ background: 'radial-gradient(circle, #fff5d6 0%, rgba(255,245,214,0.4) 40%, transparent 70%)' }} />

        <div className="relative max-w-5xl mx-auto px-6 py-24 w-full">
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <p className="text-[10px] tracking-[0.4em] uppercase text-amber-200">A selected invitation · Early October 2026</p>
            <span className="hidden sm:inline text-amber-200/50">·</span>
            <p className="text-[10px] tracking-[0.4em] uppercase text-amber-200 inline-flex items-center gap-1.5 border border-amber-200/60 rounded-full px-3 py-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-200" /> SUV / MUV &middot; Self-driven
            </p>
          </div>
          <h1 className="text-6xl sm:text-7xl leading-[0.98] text-white mb-6" style={pf}>
            The Longer Road<br/>
            <span className="italic text-amber-200/90">Tirthan Valley &rsquo;26</span>
          </h1>
          <p className="text-xl text-stone-100 leading-relaxed max-w-[620px]" style={pf}>
            Twelve couples. Three unhurried nights on the same river.<br className="hidden sm:inline"/>
            One long weekend for the couples who wave at each other on mountain backroads.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <button onClick={() => setRequestOpen(true)}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-200 text-stone-900 font-semibold text-sm hover:bg-amber-100 transition shadow-lg shadow-amber-200/20"
                    data-testid="longer-road-request-hero">
              <Send className="h-4 w-4" /> Request to book
            </button>
            <a href={WA_1} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/30 text-white font-medium text-sm hover:bg-white/10 transition"
               data-testid="longer-road-whatsapp-1">
              <MessageCircle className="h-4 w-4" /> +91 98711 55963
            </a>
            <a href={WA_2} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/30 text-white font-medium text-sm hover:bg-white/10 transition"
               data-testid="longer-road-whatsapp-2">
              <MessageCircle className="h-4 w-4" /> +91 98117 63700
            </a>
            <Link to="/itineraries/longer-road/poster"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white/70 hover:text-white text-sm underline underline-offset-4">
              <ImageIcon className="h-4 w-4" /> Get the WhatsApp poster
            </Link>
          </div>

          {/* Signature line, on the hero */}
          <div className="mt-14 pt-6 border-t border-white/15 max-w-[560px]">
            <p className="text-[10px] tracking-[0.45em] uppercase text-amber-200/80 mb-2">Est. 2011 · Tirthan Valley</p>
            <p className="text-lg sm:text-xl italic text-white/90 leading-snug" style={pf}>
              Fifteen Octobers on this river. This is the one weekend we open the doors together.
            </p>
          </div>
        </div>
      </section>

      {/* Cottage photo band removed on request — hero alone carries the visual. */}

      {/* The Letter · editorial · with subtle Bluehouse photo splash */}
      <section className="relative py-24 px-6 bg-[#FAFAF8] overflow-hidden">
        {/* Photo splash — subtle, behind the text · desktop only */}
        {(() => {
          const bh = (u) => `https://gohimalayan.com/api/apply/photos/${u}`;
          const splash = [
            { url: bh('fd236c5a-24ca-4aeb-879f-f179676f47f6'), cls: 'top-16 left-4 lg:left-16 w-40 lg:w-48 -rotate-6' },
            { url: bh('e9351a4a-f28b-4bec-828e-be2971f4eafc'), cls: 'top-40 right-6 lg:right-20 w-44 lg:w-56 rotate-3' },
            { url: bh('12bea83e-d699-40f0-aaca-45f47d4b1add'), cls: 'bottom-40 left-6 lg:left-24 w-40 lg:w-52 rotate-6' },
            { url: bh('afda80de-2d46-44a2-8b28-1ab52b14288c'), cls: 'bottom-20 right-4 lg:right-16 w-40 lg:w-48 -rotate-4' },
          ];
          return splash.map((s, i) => (
            <div key={i}
                 className={`hidden md:block absolute ${s.cls} bg-white p-2 shadow-xl pointer-events-none`}
                 style={{ opacity: 0.6, transform: `translateZ(0) rotate(${s.cls.includes('-rotate-6') ? '-6deg' : s.cls.includes('rotate-6') ? '6deg' : s.cls.includes('-rotate-4') ? '-4deg' : '3deg'})` }}
                 aria-hidden>
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={s.url} alt="" className="w-full h-full object-cover" style={{ filter: 'saturate(0.85) contrast(0.95)' }} loading="lazy" />
                {/* faint cream wash so photos read as "faded imagery" behind the letter */}
                <div className="absolute inset-0" style={{ backgroundColor: 'rgba(250,250,248,0.25)' }} />
              </div>
            </div>
          ));
        })()}

        <div className="relative max-w-3xl mx-auto">
          <p className="text-[10px] tracking-[0.45em] uppercase mb-8" style={{ color: GH_BLUE }}>A letter from the founders</p>
          <p className="text-[24px] sm:text-[28px] leading-[1.35] text-stone-900 mb-8 italic" style={pf}>
            We&rsquo;ve been doing this quietly for fifteen Octobers. This one, we&rsquo;re inviting a few of you along.
          </p>
          <div className="space-y-6 text-[18px] text-stone-700 leading-[1.75]" style={{ ...pf, fontStyle: 'normal' }}>
            <p>
              In 2011 we built a small stone-and-wood house on a bend of the Tirthan &mdash; the kind of place you drive fifteen kilometres past just to earn the arrival. We&rsquo;ve run it, quietly, for the fifteen Octobers since. Some of you have stayed there. Most of you have not.
            </p>
            <p>
              In the same years, GoHimalayan has become the slow, editorial map to everywhere else we&rsquo;ve fallen for. The deodar forests where the last bus stops turning up. The trout streams that don&rsquo;t have names on signboards. The high village terraces where the fifth cup of tea is still hot, because there&rsquo;s no one else around to want one.
            </p>
            <p>
              Quiet luxury, for us, has never been a spa menu. It&rsquo;s a good bed inside a stone wall, a river that runs all night, a host who remembers how you take your coffee, and hours that don&rsquo;t need filling.
            </p>
            <p>
              This October we&rsquo;re opening our river house and a small sister property up the valley &mdash; also riverside, also unnamed on purpose &mdash; for one long weekend. No brand tents. No timed anything. Just the two of you, one river, and a few things we&rsquo;ve been quietly saving for guests we didn&rsquo;t have to explain any of it to.
            </p>
          </div>
        </div>
      </section>

      {/* Valley colours · photo grid */}
      {/* removed on request — replaced by single Tirthan photo band above */}

      {/* Faded mountain-road strip — sets the mood for the itinerary below */}
      <section className="relative py-24 sm:py-28 px-6 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=2000&q=80"
          alt="Himalayan mountains above the clouds"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.62) saturate(0.9) contrast(1.02)' }}
        />
        <div className="absolute inset-0"
             style={{ background: 'linear-gradient(180deg, rgba(11,26,44,0.55) 0%, rgba(11,26,44,0.35) 40%, rgba(11,26,44,0.75) 100%)' }} />
        <div className="relative max-w-3xl mx-auto text-center">
          <p className="text-[10px] tracking-[0.5em] uppercase text-amber-200 mb-5">The weekend, in outline</p>
          <p className="text-xl sm:text-2xl leading-relaxed text-stone-100" style={pf}>
            Thursday morning out of Delhi, Sunday home &mdash; four days end-to-end. One flag-off, one map on WhatsApp forty-eight hours before we drive.
          </p>
          <p className="mt-6 text-[15px] italic text-amber-200/90" style={pf}>
            Welcome to the Longer Road. Drive with us.
          </p>
        </div>
      </section>

      {/* Schedule */}
      <section className="py-16 px-6 bg-[#FAFAF8]">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-10">
            {SCHEDULE.map((s) => (
              <div key={s.day} className="grid grid-cols-1 sm:grid-cols-[110px_1fr] gap-3 sm:gap-6">
                <p className="text-[11px] tracking-[0.28em] uppercase font-semibold" style={{ color: GH_BLUE }}>{s.day}</p>
                <div>
                  <p className="text-[18px] text-stone-900 mb-2" style={pf}>{s.title}</p>
                  <p className="text-[14px] text-stone-600 leading-relaxed mb-4">{s.body}</p>
                  {s.highlight && (
                    <div className="mt-3 pl-4 border-l-2" style={{ borderColor: GH_BLUE }}>
                      <p className="text-[13px] text-stone-800 leading-relaxed italic" style={pf}>
                        {s.highlight}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open menu — things you could do while you're here */}
      <section className="py-16 px-6 bg-[#FAFAF8] border-t border-stone-200">
        <div className="max-w-3xl mx-auto">
          <p className="text-[10px] tracking-[0.35em] uppercase mb-3" style={{ color: GH_BLUE }}>An open menu</p>
          <p className="text-stone-500 text-sm mb-8 italic max-w-[640px]" style={pf}>
            The rest is yours. Below is what the valley opens up to, on your own time &mdash; not fixed to any day, not routed by anyone. Pick, skip, sleep through them. We&rsquo;ll set up any of them on request.
          </p>
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-6">
            {OPTIONAL_EXPERIENCES.map((e) => (
              <div key={e.name} className="border-l-2 pl-4" style={{ borderColor: '#E5D8B8' }}>
                <p className="text-[16px] text-stone-900 mb-1" style={pf}>{e.name}</p>
                <p className="text-[13px] text-stone-600 leading-relaxed">{e.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Price + Included */}
      <section className="py-16 px-6 bg-[#FAFAF8]">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl p-8 mb-8 text-white" style={{ backgroundColor: GH_BLUE }}>
            <p className="text-[10px] tracking-[0.4em] uppercase text-amber-200 mb-4">Per couple, double occupancy</p>

            {/* Two-tier pricing */}
            <div className="grid sm:grid-cols-2 gap-6 sm:gap-8 mb-5">
              {/* Early-bird — featured */}
              <div className="relative rounded-xl p-5 border-2" style={{ borderColor: '#EBC773', backgroundColor: 'rgba(235,199,115,0.10)' }}>
                <span className="absolute -top-2.5 left-4 px-2 py-0.5 rounded-full text-[9px] tracking-[0.25em] uppercase font-bold"
                      style={{ backgroundColor: '#EBC773', color: '#0f1620' }}>
                  Early bird
                </span>
                <p className="text-[10px] tracking-[0.3em] uppercase text-amber-200 mt-1 mb-1">Book by 10 September</p>
                <p className="text-[44px] sm:text-[48px] font-light leading-none" style={pf}>&#8377;42,000</p>
              </div>
              {/* Regular */}
              <div className="rounded-xl p-5 border border-white/25">
                <p className="text-[10px] tracking-[0.3em] uppercase text-stone-300 mt-1 mb-1">After 10 September</p>
                <p className="text-[44px] sm:text-[48px] font-light leading-none text-white/80" style={pf}>&#8377;60,000</p>
              </div>
            </div>

            <p className="text-sm text-stone-100">3 nights &middot; 4 days &middot; all-inclusive except fuel &amp; alcohol</p>
            <p className="text-xs text-amber-200 mt-3 italic">Deposit &#8377;15,000 on RSVP &middot; balance 15 days before we drive.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-3">Included</p>
              <ul className="space-y-2">
                {INCLUDED.map((i) => (
                  <li key={i} className="flex gap-2 text-[13px] text-stone-700">
                    <Check className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: GH_BLUE }} strokeWidth={3} />
                    <span>{i}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-3">Not included</p>
              <ul className="space-y-2">
                {EXCLUDED.map((i) => (
                  <li key={i} className="flex gap-2 text-[13px] text-stone-600">
                    <X className="h-4 w-4 flex-shrink-0 mt-0.5 text-stone-400" />
                    <span>{i}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* RSVP · ridge image + amber wash */}
      <section className="relative py-24 px-6 overflow-hidden">
        <img src={IMG_RIDGE} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ filter: 'brightness(0.35) saturate(1.15)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(11,18,32,0.7) 0%, rgba(180,100,50,0.35) 60%, rgba(11,18,32,0.9) 100%)' }} />
        <div className="relative max-w-2xl mx-auto text-center">
          <p className="text-[10px] tracking-[0.4em] uppercase mb-4 text-amber-200">12 couples only · closes 10 September</p>
          <h2 className="text-4xl mb-4 text-white" style={pf}>Tap to say &lsquo;count us in&rsquo;</h2>
          <p className="text-sm text-stone-200 mb-8">Tell us who you are and where you&rsquo;re driving from. We&rsquo;ll take it from there.</p>
          <div className="flex justify-center">
            <button onClick={() => setRequestOpen(true)}
                    className="inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full font-semibold hover:brightness-105 transition shadow-lg shadow-emerald-900/30 whitespace-nowrap text-white"
                    style={{ backgroundColor: '#25D366' }}
                    data-testid="longer-road-request-bottom">
              <MessageCircle className="h-5 w-5" /> Request to book
            </button>
          </div>
          <p className="text-[11px] text-white/50 mt-10 italic">Independently curated by the founders of Bluehouse &amp; GoHimalayan. Not affiliated with any vehicle manufacturer.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-950 text-stone-500 py-8 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between text-[10px] tracking-[0.3em] uppercase">
          <span>&copy; 2026 GoHimalayan</span>
          <Link to="/itineraries" className="hover:text-amber-200">&larr; All itineraries</Link>
        </div>
      </footer>

      {/* Request to book modal */}
      <LongerRoadRequestModal open={requestOpen} onClose={() => setRequestOpen(false)} />
    </div>
  );
}
